/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
 */
export const UpdateSigintSensorFormInputStrategy = {
  SingleMessage: "single_message",
  SlidingWindow: "sliding_window",
} as const;
/**
 * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
 */
export type UpdateSigintSensorFormInputStrategy = ClosedEnum<
  typeof UpdateSigintSensorFormInputStrategy
>;

/**
 * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
 */
export const UpdateSigintSensorFormMode = {
  MultiLabel: "multi_label",
  Exclusive: "exclusive",
  OrderedScore: "ordered_score",
} as const;
/**
 * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
 */
export type UpdateSigintSensorFormMode = ClosedEnum<
  typeof UpdateSigintSensorFormMode
>;

export type UpdateSigintSensorForm = {
  /**
   * Replacement description; empty clears it
   */
  description?: string | undefined;
  /**
   * Enable or disable evaluation; omission preserves the current state. Evaluations already in progress may finish.
   */
  enabled?: boolean | undefined;
  /**
   * Sensor ID
   */
  id: string;
  /**
   * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
   */
  inputStrategy?: UpdateSigintSensorFormInputStrategy | undefined;
  /**
   * Replacement instructions; empty clears it
   */
  instructions?: string | undefined;
  /**
   * Replacement boolean CEL predicate, at most 4096 UTF-8 bytes; omission preserves it
   */
  matchExpression?: string | undefined;
  /**
   * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
   */
  mode?: UpdateSigintSensorFormMode | undefined;
  /**
   * Replacement display name; trimmed before enforcing the 1 to 200 character limit
   */
  name?: string | undefined;
  /**
   * Authoritative ordered signal IDs. Omit or send null to preserve; send [] to clear.
   */
  signalIds?: Array<string> | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  slug?: string | undefined;
  /**
   * Replacement total sliding window messages, including the target; omission preserves it
   */
  windowSize?: number | undefined;
};

/** @internal */
export const UpdateSigintSensorFormInputStrategy$outboundSchema: z.ZodMiniEnum<
  typeof UpdateSigintSensorFormInputStrategy
> = z.enum(UpdateSigintSensorFormInputStrategy);

/** @internal */
export const UpdateSigintSensorFormMode$outboundSchema: z.ZodMiniEnum<
  typeof UpdateSigintSensorFormMode
> = z.enum(UpdateSigintSensorFormMode);

/** @internal */
export type UpdateSigintSensorForm$Outbound = {
  description?: string | undefined;
  enabled?: boolean | undefined;
  id: string;
  input_strategy?: string | undefined;
  instructions?: string | undefined;
  match_expression?: string | undefined;
  mode?: string | undefined;
  name?: string | undefined;
  signal_ids?: Array<string> | undefined;
  slug?: string | undefined;
  window_size?: number | undefined;
};

/** @internal */
export const UpdateSigintSensorForm$outboundSchema: z.ZodMiniType<
  UpdateSigintSensorForm$Outbound,
  UpdateSigintSensorForm
> = z.pipe(
  z.object({
    description: z.optional(z.string()),
    enabled: z.optional(z.boolean()),
    id: z.string(),
    inputStrategy: z.optional(
      UpdateSigintSensorFormInputStrategy$outboundSchema,
    ),
    instructions: z.optional(z.string()),
    matchExpression: z.optional(z.string()),
    mode: z.optional(UpdateSigintSensorFormMode$outboundSchema),
    name: z.optional(z.string()),
    signalIds: z.optional(z.array(z.string())),
    slug: z.optional(z.string()),
    windowSize: z.optional(z.int()),
  }),
  z.transform((v) => {
    return remap$(v, {
      inputStrategy: "input_strategy",
      matchExpression: "match_expression",
      signalIds: "signal_ids",
      windowSize: "window_size",
    });
  }),
);

export function updateSigintSensorFormToJSON(
  updateSigintSensorForm: UpdateSigintSensorForm,
): string {
  return JSON.stringify(
    UpdateSigintSensorForm$outboundSchema.parse(updateSigintSensorForm),
  );
}
